import React, { useEffect, useMemo, useState } from 'react';
import {
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  FormControl,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import {
  Add as AddIcon,
  ArrowDropDown as ArrowDropDownIcon,
  DeleteOutline as DeleteOutlineIcon,
} from '@mui/icons-material';
import SearchableSelect from '../SearchableSelect';
import ClienteInlineSearchField from '../Tesoreria/ClienteInlineSearchField';
import ClienteSearch from '../Tesoreria/ClienteSearch';
import ProveedorInlineSearchField from '../ProveedorInlineSearchField';
import ProveedorSearch from '../ProveedorSearch';
import ProductoSearch from '../ProductoSearch';
import LocalidadSearch from '../LocalidadSearch';
import VendedorSelect from '../VendedorSelect';
import productoService, { PARAMS_PRODUCTO_OPERATIVO, Producto } from '../../services/productoService';
import { catalogosService } from '../../services/catalogosService';
import provinciaService, { Provincia } from '../../services/provinciaService';
import ubicacionService, { etiquetaUbicacionSeleccion, Ubicacion } from '../../services/ubicacionService';
import { FORMAS_DE_PAGO_VENTA } from '../../constants/formasDePagoVenta';
import type { SucursalVenta } from '../../services/sucursalesVentaService';
import type { TipoFormulario } from '../../services/tipoFormularioService';
import type { UsuarioEmpresaOpcion } from '../../utils/informePersonalizadoDefinicion';
import type { Clase, Linea, Ramo } from '../../types';
import type { InformeField, InformeFilter, InformeFilterOperator } from './informeDinamicoUtils';
import {
  admiteVariosValores,
  camposFiltroInforme,
  filtraPorColumnasDelInforme,
  filtroAdicionalColumnaNuevo,
  filtroAdicionalVentaNuevo,
  guardarListaValores,
  leerListaValores,
  type ModuloFiltroInforme,
  type ModuloPanelFiltros,
  etiquetaArticulo,
  etiquetaCliente,
  guardarArticulo,
  guardarCliente,
  guardarLocalidad,
  guardarVendedor,
  leerArticulo,
  leerCliente,
  leerLocalidad,
  leerVendedor,
  muestraCondicionFiltroVenta,
  operadorAlElegirCampoColumna,
  operadorAlElegirCampoVenta,
} from './filtrosAdicionalesVentas';

const FORMAS_DE_PAGO_COMPRA = [
  'CONTADO',
  'CTA.CTE.',
  'EFECTIVO',
  'DEVOLUCION',
  'CUOTAS',
  'CONTRAREEMBO',
  'PAGOANTICIPA',
] as const;

const accionButtonSx = { textTransform: 'none', borderRadius: 2, fontWeight: 500 };
const filtroFieldSx = { '& .MuiOutlinedInput-root': { borderRadius: 2, bgcolor: '#fff' } };

function moduloCatalogo(modulo: ModuloPanelFiltros): ModuloFiltroInforme {
  return modulo === 'Compras' ? 'Compras' : 'Ventas';
}

interface FiltrosAdicionalesVentasProps {
  modulo?: ModuloPanelFiltros;
  filtros: InformeFilter[];
  campos: InformeField[];
  sucursales: SucursalVenta[];
  loadingSucursales: boolean;
  tiposFormulario: TipoFormulario[];
  loadingTipos: boolean;
  usuariosEmpresa: UsuarioEmpresaOpcion[];
  onChange: (filtros: InformeFilter[]) => void;
  nuevoId: () => number;
  errorDe: (clave: string) => string | undefined;
}

type BusquedaAbierta = { tipo: 'cliente' | 'articulo' | 'localidad'; filtroId: number } | null;

function mismoNombre(left: string, right: string): boolean {
  return left.trim().localeCompare(right.trim(), 'es', { sensitivity: 'accent' }) === 0;
}

const FiltrosAdicionalesVentas: React.FC<FiltrosAdicionalesVentasProps> = ({
  modulo = 'Ventas',
  filtros,
  campos,
  sucursales,
  loadingSucursales,
  tiposFormulario,
  loadingTipos,
  usuariosEmpresa,
  onChange,
  nuevoId,
  errorDe,
}) => {
  const [busqueda, setBusqueda] = useState<BusquedaAbierta>(null);
  const [ramos, setRamos] = useState<Ramo[]>([]);
  const [lineas, setLineas] = useState<Linea[]>([]);
  const [clases, setClases] = useState<Clase[]>([]);
  const [provincias, setProvincias] = useState<Provincia[]>([]);
  const [ubicaciones, setUbicaciones] = useState<Ubicacion[]>([]);
  const [cargandoRamos, setCargandoRamos] = useState(false);
  const [cargandoLineas, setCargandoLineas] = useState(false);
  const [cargandoClases, setCargandoClases] = useState(false);
  const [cargandoProvincias, setCargandoProvincias] = useState(false);
  const [cargandoUbicaciones, setCargandoUbicaciones] = useState(false);

  const necesitaRamos = filtros.some((filtro) => (
    filtro.fieldId === 'ramo' || filtro.fieldId === 'linea' || filtro.fieldId === 'clase'
  ));
  const necesitaLineas = filtros.some((filtro) => filtro.fieldId === 'linea' || filtro.fieldId === 'clase');
  const necesitaClases = filtros.some((filtro) => filtro.fieldId === 'clase');
  const necesitaProvincias = filtros.some((filtro) => filtro.fieldId === 'localidad');
  const necesitaUbicaciones = filtros.some((filtro) => filtro.fieldId === 'ubicacion');
  const ramoElegido = filtros.find((filtro) => filtro.fieldId === 'ramo' && filtro.value.trim())?.value ?? '';
  const lineaElegida = filtros.find((filtro) => filtro.fieldId === 'linea' && filtro.value.trim())?.value ?? '';
  const codigoRamo = ramos.find((ramo) => mismoNombre(ramo.nombreRamo, ramoElegido))?.codigoRamo;
  const codigoLinea = lineas.find((linea) => mismoNombre(linea.nombreLinea, lineaElegida))?.codigoLinea;

  useEffect(() => {
    if (!necesitaRamos) return undefined;
    let cancelado = false;
    setCargandoRamos(true);
    catalogosService.getRamos(1, 500)
      .then((resultado) => {
        if (!cancelado) setRamos(resultado.items ?? []);
      })
      .catch(() => {
        if (!cancelado) setRamos([]);
      })
      .finally(() => {
        if (!cancelado) setCargandoRamos(false);
      });
    return () => {
      cancelado = true;
    };
  }, [necesitaRamos]);

  useEffect(() => {
    if (!necesitaLineas) return undefined;
    let cancelado = false;
    setCargandoLineas(true);
    catalogosService.getLineas(1, 500, undefined, codigoRamo)
      .then((resultado) => {
        if (!cancelado) setLineas(resultado.items ?? []);
      })
      .catch(() => {
        if (!cancelado) setLineas([]);
      })
      .finally(() => {
        if (!cancelado) setCargandoLineas(false);
      });
    return () => {
      cancelado = true;
    };
  }, [codigoRamo, necesitaLineas]);

  useEffect(() => {
    if (!necesitaClases) return undefined;
    let cancelado = false;
    setCargandoClases(true);
    catalogosService.getClases(1, 500, undefined, codigoRamo, codigoLinea)
      .then((resultado) => {
        if (!cancelado) setClases(resultado.items ?? []);
      })
      .catch(() => {
        if (!cancelado) setClases([]);
      })
      .finally(() => {
        if (!cancelado) setCargandoClases(false);
      });
    return () => {
      cancelado = true;
    };
  }, [codigoLinea, codigoRamo, necesitaClases]);

  useEffect(() => {
    if (!necesitaProvincias) return undefined;
    let cancelado = false;
    setCargandoProvincias(true);
    provinciaService.getProvincias({ pageNumber: 1, pageSize: 500 })
      .then((resultado) => {
        if (!cancelado) setProvincias(resultado.items ?? []);
      })
      .catch(() => {
        if (!cancelado) setProvincias([]);
      })
      .finally(() => {
        if (!cancelado) setCargandoProvincias(false);
      });
    return () => {
      cancelado = true;
    };
  }, [necesitaProvincias]);

  useEffect(() => {
    if (!necesitaUbicaciones) return undefined;
    let cancelado = false;
    setCargandoUbicaciones(true);
    ubicacionService.getUbicaciones({ pageNumber: 1, pageSize: 500 })
      .then((resultado) => {
        if (!cancelado) setUbicaciones(resultado ?? []);
      })
      .catch(() => {
        if (!cancelado) setUbicaciones([]);
      })
      .finally(() => {
        if (!cancelado) setCargandoUbicaciones(false);
      });
    return () => {
      cancelado = true;
    };
  }, [necesitaUbicaciones]);

  const actualizar = (id: number, patch: Partial<InformeFilter>) => {
    onChange(filtros.map((filtro) => (filtro.id === id ? { ...filtro, ...patch } : filtro)));
  };

  const porColumnas = filtraPorColumnasDelInforme(modulo);
  const catalogo = porColumnas
    ? campos.map((campo) => ({ id: campo.id, label: campo.label }))
    : camposFiltroInforme(moduloCatalogo(modulo));
  const opcionesCampo = (fieldId: string) => {
    if (catalogo.some((campo) => campo.id === fieldId)) return catalogo;
    const legacy = campos.find((campo) => campo.id === fieldId);
    return [
      { id: fieldId, label: legacy?.label ?? fieldId },
      ...catalogo,
    ];
  };

  const cerrarBusqueda = () => setBusqueda(null);

  return (
    <Stack spacing={1.5}>
      <Stack direction={{ xs: 'column', sm: 'row' }} alignItems={{ xs: 'stretch', sm: 'center' }} justifyContent="space-between" spacing={1}>
        <Typography variant="body2" color="text.secondary">
          {porColumnas
            ? 'Elegí una columna del origen. En “Es igual a” o “Contiene” podés cargar varios valores: alcanza con que la fila coincida con uno.'
            : `${modulo === 'Ventas' ? 'Cliente' : 'Proveedor'} y artículo se eligen con el buscador. Las listas y los rangos se aplican sobre las filas ya cargadas.`}
        </Typography>
        <Button
          startIcon={<AddIcon />}
          variant="outlined"
          sx={accionButtonSx}
          onClick={() => {
            if (!porColumnas) {
              onChange([...filtros, filtroAdicionalVentaNuevo(nuevoId(), moduloCatalogo(modulo))]);
              return;
            }
            const campo = campos.find((item) => item.type === 'text') ?? campos[0];
            if (!campo) return;
            onChange([...filtros, filtroAdicionalColumnaNuevo(nuevoId(), campo.id, campo.type)]);
          }}
        >
          Agregar filtro
        </Button>
      </Stack>
      {filtros.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          No agregaste filtros adicionales.
        </Typography>
      ) : (
        <Stack spacing={1.5}>
          {filtros.map((filtro, index) => (
            <Stack key={filtro.id} direction={{ xs: 'column', md: 'row' }} spacing={1.5} alignItems={{ md: 'flex-start' }}>
              <FormControl size="small" sx={{ width: { xs: '100%', md: 220 }, flexShrink: 0 }}>
                <InputLabel id={`filter-field-label-${filtro.id}`}>Campo</InputLabel>
                <Select
                  id={`filter-field-${filtro.id}`}
                  labelId={`filter-field-label-${filtro.id}`}
                  label="Campo"
                  value={filtro.fieldId}
                  onChange={(event) => {
                    const fieldId = event.target.value;
                    const tipo = campos.find((campo) => campo.id === fieldId)?.type ?? 'text';
                    actualizar(filtro.id, {
                      fieldId,
                      operator: porColumnas
                        ? operadorAlElegirCampoColumna(tipo)
                        : operadorAlElegirCampoVenta(fieldId),
                      value: '',
                      valorHasta: '',
                    });
                  }}
                >
                  {opcionesCampo(filtro.fieldId).map((campo) => (
                    <MenuItem key={campo.id} value={campo.id}>{campo.label}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              {porColumnas ? (
                <TextField
                  select
                  size="small"
                  label="Condición"
                  value={filtro.operator}
                  onChange={(event) => {
                    const operator = event.target.value as InformeFilterOperator;
                    const patch: Partial<InformeFilter> = { operator };
                    if (!admiteVariosValores(operator)) {
                      patch.value = leerListaValores(filtro.value)[0] ?? '';
                    }
                    actualizar(filtro.id, patch);
                  }}
                  SelectProps={{ native: true }}
                  sx={{ minWidth: { md: 190 }, flex: { md: '0 0 190px' }, ...filtroFieldSx }}
                >
                  {(campos.find((campo) => campo.id === filtro.fieldId)?.type ?? 'text') === 'text' ? (
                    <>
                      <option value="contains">Contiene</option>
                      <option value="equals">Es igual a</option>
                    </>
                  ) : (
                    <>
                      <option value="equals">Es igual a</option>
                      <option value="greaterThan">Mayor o igual que</option>
                      <option value="lessThan">Menor o igual que</option>
                      <option value="between">Entre</option>
                    </>
                  )}
                </TextField>
              ) : muestraCondicionFiltroVenta(filtro.fieldId) && (
                <TextField
                  select
                  size="small"
                  label="Condición"
                  value={filtro.operator}
                  onChange={(event) => actualizar(filtro.id, {
                    operator: event.target.value as InformeFilterOperator,
                  })}
                  SelectProps={{ native: true }}
                  sx={{ minWidth: { md: 190 }, flex: { md: '0 0 190px' }, ...filtroFieldSx }}
                >
                  {filtro.fieldId === 'comprobante' ? (
                    <>
                      {filtro.operator === 'contains' && <option value="contains">Contiene</option>}
                      <option value="equals">Es igual a</option>
                      <option value="greaterThan">Mayor o igual que</option>
                      <option value="lessThan">Menor o igual que</option>
                      <option value="between">Entre</option>
                    </>
                  ) : (
                    <>
                      {(filtro.operator === 'equals' || filtro.operator === 'contains') && (
                        <option value={filtro.operator}>
                          {filtro.operator === 'equals' ? 'Es igual a' : 'Contiene'}
                        </option>
                      )}
                      <option value="greaterThan">Mayor o igual que</option>
                      <option value="lessThan">Menor o igual que</option>
                      <option value="between">Entre</option>
                    </>
                  )}
                </TextField>
              )}
              <Box sx={{ flex: 1, minWidth: 0, width: '100%' }}>
                <ValorFiltroVenta
                  modulo={modulo}
                  filtro={filtro}
                  index={index}
                  errorDe={errorDe}
                  sucursales={sucursales}
                  loadingSucursales={loadingSucursales}
                  tiposFormulario={tiposFormulario}
                  loadingTipos={loadingTipos}
                  usuariosEmpresa={usuariosEmpresa}
                  ramos={ramos}
                  lineas={lineas}
                  clases={clases}
                  provincias={provincias}
                  ubicaciones={ubicaciones}
                  cargandoRamos={cargandoRamos}
                  cargandoLineas={cargandoLineas}
                  cargandoClases={cargandoClases}
                  cargandoProvincias={cargandoProvincias}
                  cargandoUbicaciones={cargandoUbicaciones}
                  ramoElegido={Boolean(ramoElegido)}
                  lineaElegida={Boolean(lineaElegida)}
                  onChange={(patch) => actualizar(filtro.id, patch)}
                  onBuscarCliente={() => setBusqueda({ tipo: 'cliente', filtroId: filtro.id })}
                  onBuscarArticulo={() => setBusqueda({ tipo: 'articulo', filtroId: filtro.id })}
                  onBuscarLocalidad={() => setBusqueda({ tipo: 'localidad', filtroId: filtro.id })}
                  campoLegacy={campos.find((campo) => campo.id === filtro.fieldId)}
                />
              </Box>
              <Button
                aria-label="Quitar filtro"
                color="error"
                onClick={() => onChange(filtros.filter((item) => item.id !== filtro.id))}
                sx={{ mt: { md: 0.5 }, alignSelf: { xs: 'flex-end', md: 'flex-start' } }}
              >
                <DeleteOutlineIcon />
              </Button>
            </Stack>
          ))}
        </Stack>
      )}
      {modulo === 'Ventas' ? (
        <ClienteSearch
          open={busqueda?.tipo === 'cliente'}
          onClose={cerrarBusqueda}
          excluirInactivos={false}
          onSelect={(cliente) => {
            if (busqueda?.tipo === 'cliente') {
              actualizar(busqueda.filtroId, {
                operator: 'equals',
                value: guardarCliente({
                  nro: String(cliente.nroCliente),
                  nombre: cliente.nombre ?? '',
                }),
              });
            }
            cerrarBusqueda();
          }}
        />
      ) : (
        <ProveedorSearch
          open={busqueda?.tipo === 'cliente'}
          onClose={cerrarBusqueda}
          onSelect={(proveedor) => {
            if (busqueda?.tipo === 'cliente') {
              actualizar(busqueda.filtroId, {
                operator: 'equals',
                value: guardarCliente({
                  nro: String(proveedor.nroProveedor),
                  nombre: proveedor.nombre ?? '',
                }),
              });
            }
            cerrarBusqueda();
          }}
        />
      )}
      <ProductoSearch
        open={busqueda?.tipo === 'articulo'}
        onClose={cerrarBusqueda}
        onSelect={(producto) => {
          if (busqueda?.tipo === 'articulo') {
            actualizar(busqueda.filtroId, {
              operator: 'equals',
              value: guardarArticulo({
                codigo: producto.codigoArticulo ?? '',
                descripcion: producto.descripcion ?? '',
              }),
            });
          }
          cerrarBusqueda();
        }}
      />
      <LocalidadSearch
        open={busqueda?.tipo === 'localidad'}
        onClose={cerrarBusqueda}
        onSelect={(localidad) => {
          if (busqueda?.tipo === 'localidad') {
            const actual = filtros.find((filtro) => filtro.id === busqueda.filtroId);
            const previa = leerLocalidad(actual?.value ?? '');
            actualizar(busqueda.filtroId, {
              operator: 'equals',
              value: guardarLocalidad({
                ciudad: localidad.nombreCiudad?.trim() ?? '',
                provincia: localidad.nombreProvincia?.trim() || previa.provincia,
              }),
            });
          }
          cerrarBusqueda();
        }}
      />
    </Stack>
  );
};

interface ValoresVariosFiltroProps {
  tipo: 'text' | 'number';
  valor: string;
  error: boolean;
  ayuda?: string;
  onChange: (valor: string) => void;
}

function partesValor(texto: string): string[] {
  return texto.split(/[,;\n]/).map((parte) => parte.trim()).filter(Boolean);
}

function valorNumeroValido(texto: string): boolean {
  return Number.isFinite(Number(texto.trim().replace(',', '.')));
}

const ValoresVariosFiltro: React.FC<ValoresVariosFiltroProps> = ({ tipo, valor, error, ayuda, onChange }) => {
  const seleccionados = leerListaValores(valor);
  const incorporar = (entradas: string[]) => {
    const aceptados = entradas
      .flatMap(partesValor)
      .filter((parte) => (tipo === 'number' ? valorNumeroValido(parte) : Boolean(parte.trim())))
      .map((parte) => (tipo === 'number' ? parte.trim().replace(',', '.') : parte.trim()));
    onChange(guardarListaValores([...seleccionados, ...aceptados]));
  };

  return (
    <Autocomplete
      multiple
      freeSolo
      options={[] as string[]}
      value={seleccionados}
      onChange={(_, value) => {
        const textos = value.map((item) => String(item));
        const aceptados = textos
          .flatMap(partesValor)
          .filter((parte) => (tipo === 'number' ? valorNumeroValido(parte) : Boolean(parte.trim())))
          .map((parte) => (tipo === 'number' ? parte.trim().replace(',', '.') : parte.trim()));
        onChange(guardarListaValores(aceptados));
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          size="small"
          label="Valores"
          placeholder={seleccionados.length === 0 ? 'Escribí un valor y Enter' : ''}
          error={error}
          helperText={ayuda || 'Enter agrega otro. La fila entra si coincide con cualquiera.'}
          onBlur={(event) => {
            const pendiente = event.target.value.trim();
            if (pendiente) incorporar([pendiente]);
          }}
          sx={filtroFieldSx}
        />
      )}
    />
  );
};

interface ValorFiltroVentaProps {
  modulo: ModuloPanelFiltros;
  filtro: InformeFilter;
  index: number;
  errorDe: (clave: string) => string | undefined;
  sucursales: SucursalVenta[];
  loadingSucursales: boolean;
  tiposFormulario: TipoFormulario[];
  loadingTipos: boolean;
  usuariosEmpresa: UsuarioEmpresaOpcion[];
  ramos: Ramo[];
  lineas: Linea[];
  clases: Clase[];
  provincias: Provincia[];
  ubicaciones: Ubicacion[];
  cargandoRamos: boolean;
  cargandoLineas: boolean;
  cargandoClases: boolean;
  cargandoProvincias: boolean;
  cargandoUbicaciones: boolean;
  ramoElegido: boolean;
  lineaElegida: boolean;
  onChange: (patch: Partial<InformeFilter>) => void;
  onBuscarCliente: () => void;
  onBuscarArticulo: () => void;
  onBuscarLocalidad: () => void;
  campoLegacy?: InformeField;
}

const ValorFiltroVenta: React.FC<ValorFiltroVentaProps> = ({
  modulo,
  filtro,
  index,
  errorDe,
  sucursales,
  loadingSucursales,
  tiposFormulario,
  loadingTipos,
  usuariosEmpresa,
  ramos,
  lineas,
  clases,
  provincias,
  ubicaciones,
  cargandoRamos,
  cargandoLineas,
  cargandoClases,
  cargandoProvincias,
  cargandoUbicaciones,
  ramoElegido,
  lineaElegida,
  onChange,
  onBuscarCliente,
  onBuscarArticulo,
  onBuscarLocalidad,
  campoLegacy,
}) => {
  const errorValor = Boolean(errorDe(`filtros[${index}].valor`));
  const ayudaValor = errorDe(`filtros[${index}].valor`);

  if (filtraPorColumnasDelInforme(modulo)) {
    const tipo = campoLegacy?.type === 'number' || campoLegacy?.type === 'date' ? campoLegacy.type : 'text';
    if (admiteVariosValores(filtro.operator) && tipo !== 'date') {
      return (
        <ValoresVariosFiltro
          tipo={tipo}
          valor={filtro.value}
          error={errorValor}
          ayuda={ayudaValor}
          onChange={(value) => onChange({ value })}
        />
      );
    }
    if (tipo === 'text') {
      return (
        <TextField
          fullWidth
          size="small"
          label="Valor"
          value={filtro.value}
          error={errorValor}
          helperText={ayudaValor}
          onChange={(event) => onChange({ value: event.target.value })}
          sx={filtroFieldSx}
        />
      );
    }
    return (
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
        <TextField
          fullWidth
          size="small"
          label={filtro.operator === 'between' ? 'Desde' : 'Valor'}
          type={tipo}
          value={filtro.value}
          error={errorValor}
          helperText={ayudaValor}
          InputLabelProps={tipo === 'date' ? { shrink: true } : undefined}
          inputProps={tipo === 'number' ? { step: 'any' } : undefined}
          onChange={(event) => onChange({ value: event.target.value })}
          sx={filtroFieldSx}
        />
        {filtro.operator === 'between' && (
          <TextField
            fullWidth
            size="small"
            label="Hasta"
            type={tipo}
            value={filtro.valorHasta ?? ''}
            error={Boolean(errorDe(`filtros[${index}].valorHasta`))}
            helperText={errorDe(`filtros[${index}].valorHasta`)}
            InputLabelProps={tipo === 'date' ? { shrink: true } : undefined}
            inputProps={tipo === 'number' ? { step: 'any' } : undefined}
            onChange={(event) => onChange({ valorHasta: event.target.value })}
            sx={filtroFieldSx}
          />
        )}
      </Stack>
    );
  }

  if (filtro.fieldId === 'proveedor') {
    const proveedor = leerCliente(filtro.value);
    return (
      <Stack direction="row" spacing={1} alignItems="flex-start">
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <ProveedorInlineSearchField
            label="Proveedor"
            placeholder="Número, nombre o CUIT"
            size="small"
            displayValue={etiquetaCliente(proveedor)}
            onSelect={(elegido) => onChange({
              operator: 'equals',
              value: guardarCliente({
                nro: String(elegido.nroProveedor),
                nombre: elegido.nombre ?? '',
              }),
            })}
            onOpenModal={onBuscarCliente}
          />
        </Box>
        {etiquetaCliente(proveedor) && (
          <Button size="small" onClick={() => onChange({ value: '' })} sx={{ mt: 0.5, textTransform: 'none', flexShrink: 0 }}>
            Limpiar
          </Button>
        )}
      </Stack>
    );
  }

  if (filtro.fieldId === 'cliente') {
    const cliente = leerCliente(filtro.value);
    return (
      <Stack direction="row" spacing={1} alignItems="flex-start">
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <ClienteInlineSearchField
            label="Cliente"
            placeholder="Número, nombre o CUIT"
            size="small"
            excluirInactivos={false}
            displayValue={etiquetaCliente(cliente)}
            onSelect={(elegido) => onChange({
              operator: 'equals',
              value: guardarCliente({
                nro: String(elegido.nroCliente),
                nombre: elegido.nombre ?? '',
              }),
            })}
            onOpenModal={onBuscarCliente}
          />
        </Box>
        {etiquetaCliente(cliente) && (
          <Button size="small" onClick={() => onChange({ value: '' })} sx={{ mt: 0.5, textTransform: 'none', flexShrink: 0 }}>
            Limpiar
          </Button>
        )}
      </Stack>
    );
  }

  if (filtro.fieldId === 'articulo') {
    return (
      <ArticuloFiltroField
        valor={filtro.value}
        onElegir={(codigo, descripcion) => onChange({
          operator: 'equals',
          value: guardarArticulo({ codigo, descripcion }),
        })}
        onAbrirModal={onBuscarArticulo}
      />
    );
  }

  if (filtro.fieldId === 'localidad') {
    const localidad = leerLocalidad(filtro.value);
    const provinciaActual = localidad.provincia.trim();
    const opcionesProvincia = provincias
      .map((provincia) => provincia.nombreProvincia?.trim() ?? '')
      .filter(Boolean);
    if (provinciaActual && !opcionesProvincia.some((nombre) => mismoNombre(nombre, provinciaActual))) {
      opcionesProvincia.unshift(provinciaActual);
    }
    return (
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
        <SearchableSelect
          label="Provincia"
          size="small"
          allowEmpty
          emptyOptionLabel="Todas"
          loading={cargandoProvincias}
          value={provinciaActual || null}
          onChange={(value) => onChange({
            operator: 'equals',
            value: guardarLocalidad({ ciudad: '', provincia: value == null ? '' : String(value) }),
          })}
          options={opcionesProvincia.map((nombre) => ({ value: nombre, label: nombre }))}
          sx={filtroFieldSx}
        />
        <TextField
          fullWidth
          size="small"
          label="Ciudad"
          value={localidad.ciudad}
          placeholder="Opcional: una ciudad de esa provincia"
          onChange={(event) => onChange({
            operator: 'equals',
            value: guardarLocalidad({ ciudad: event.target.value, provincia: localidad.provincia }),
          })}
          sx={filtroFieldSx}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton
                  edge="end"
                  size="small"
                  color="primary"
                  title="Buscar localidad"
                  onMouseDown={(event) => event.preventDefault()}
                  onClick={onBuscarLocalidad}
                >
                  <ArrowDropDownIcon />
                </IconButton>
              </InputAdornment>
            ),
          }}
        />
      </Stack>
    );
  }

  if (filtro.fieldId === 'tipo') {
    const tipoActual = filtro.value.trim();
    const opcionesTipo = tiposFormulario.map((tipo) => ({
      value: tipo.nroTipoFormulario,
      label: tipo.descripcion?.trim()
        ? `${tipo.nroTipoFormulario} — ${tipo.descripcion.trim()}`
        : tipo.nroTipoFormulario,
    }));
    if (tipoActual && !opcionesTipo.some((tipo) => mismoNombre(tipo.value, tipoActual))) {
      opcionesTipo.unshift({ value: tipoActual, label: tipoActual });
    }
    return (
      <SearchableSelect
        label="Tipo de comprobante"
        size="small"
        allowEmpty
        emptyOptionLabel="Todos"
        loading={loadingTipos}
        value={tipoActual || null}
        onChange={(value) => onChange({ operator: 'equals', value: value == null ? '' : String(value) })}
        options={opcionesTipo}
        error={errorValor}
        helperText={ayudaValor}
        sx={filtroFieldSx}
      />
    );
  }

  if (filtro.fieldId === 'sucursal') {
    const nro = filtro.value.trim() ? Number(filtro.value) : null;
    return (
      <SearchableSelect
        label="Sucursal"
        size="small"
        allowEmpty
        emptyOptionLabel="Todas"
        loading={loadingSucursales}
        value={nro != null && Number.isFinite(nro) ? nro : null}
        onChange={(value) => onChange({ operator: 'equals', value: value == null ? '' : String(value) })}
        options={sucursales.map((sucursal) => ({
          value: sucursal.nroSuc,
          label: sucursal.nombreSucursal?.trim()
            ? `${String(sucursal.nroSuc).padStart(5, '0')} — ${sucursal.nombreSucursal.trim()}`
            : String(sucursal.nroSuc).padStart(5, '0'),
        }))}
        sx={filtroFieldSx}
      />
    );
  }

  if (filtro.fieldId === 'ubicacion') {
    const nro = filtro.value.trim() ? Number(filtro.value) : null;
    return (
      <SearchableSelect
        label="Ubicación"
        size="small"
        allowEmpty
        emptyOptionLabel="Todas"
        loading={cargandoUbicaciones}
        value={nro != null && Number.isFinite(nro) ? nro : null}
        onChange={(value) => onChange({ operator: 'equals', value: value == null ? '' : String(value) })}
        options={ubicaciones.map((ubicacion) => ({
          value: ubicacion.nroUbicacion,
          label: etiquetaUbicacionSeleccion(ubicacion),
        }))}
        sx={filtroFieldSx}
      />
    );
  }

  if (filtro.fieldId === 'vendedor') {
    const vendedor = leerVendedor(filtro.value);
    const nro = Number(vendedor.nro);
    return (
      <VendedorSelect
        label="Vendedor"
        size="small"
        allowEmpty
        emptyOptionLabel="Todos"
        value={Number.isInteger(nro) && nro > 0 ? nro : null}
        nombreVendedorFallback={vendedor.nombre}
        onChange={(nroVendedor, elegido) => onChange({
          operator: 'equals',
          value: guardarVendedor({
            nro: nroVendedor == null ? '' : String(nroVendedor),
            nombre: elegido?.nombreVendedor?.trim() ?? '',
          }),
        })}
        sx={filtroFieldSx}
      />
    );
  }

  if (filtro.fieldId === 'formaPago') {
    const actual = filtro.value.trim();
    const formas = modulo === 'Ventas' ? FORMAS_DE_PAGO_VENTA : FORMAS_DE_PAGO_COMPRA;
    const opciones = formas.map((forma) => forma as string);
    if (actual && !opciones.some((forma) => mismoNombre(forma, actual))) opciones.unshift(actual);
    return (
      <SearchableSelect
        label="Forma de pago"
        size="small"
        allowEmpty
        emptyOptionLabel="Todas"
        value={actual || null}
        onChange={(value) => onChange({ operator: 'equals', value: value == null ? '' : String(value) })}
        options={opciones.map((forma) => ({ value: forma, label: forma }))}
        sx={filtroFieldSx}
      />
    );
  }

  if (filtro.fieldId === 'usuario') {
    const usuarioActual = filtro.value.trim();
    const opciones = usuariosEmpresa
      .filter((usuario) => usuario.usuario.trim())
      .map((usuario) => ({
        value: usuario.usuario,
        label: usuario.nombre.trim()
          ? `${usuario.nombre.trim()} (${usuario.usuario})`
          : usuario.usuario,
      }));
    if (usuarioActual && !opciones.some((usuario) => mismoNombre(usuario.value, usuarioActual))) {
      opciones.unshift({ value: usuarioActual, label: usuarioActual });
    }
    return (
      <SearchableSelect
        label="Usuario"
        size="small"
        allowEmpty
        emptyOptionLabel="Todos"
        value={usuarioActual || null}
        onChange={(value) => onChange({ operator: 'equals', value: value == null ? '' : String(value) })}
        options={opciones}
        sx={filtroFieldSx}
      />
    );
  }

  if (filtro.fieldId === 'ramo' || filtro.fieldId === 'linea' || filtro.fieldId === 'clase') {
    const catalogo = filtro.fieldId === 'ramo'
      ? ramos.map((ramo) => ramo.nombreRamo)
      : filtro.fieldId === 'linea'
        ? lineas.map((linea) => linea.nombreLinea)
        : clases.map((clase) => clase.nombreClase);
    const nombres = Array.from(new Set(catalogo.map((nombre) => nombre?.trim() ?? '').filter(Boolean)));
    if (filtro.value.trim() && !nombres.some((nombre) => mismoNombre(nombre, filtro.value))) {
      nombres.unshift(filtro.value.trim());
    }
    const ayuda = filtro.fieldId === 'linea' && ramoElegido
      ? 'Mostrando las líneas del ramo elegido.'
      : filtro.fieldId === 'clase' && (ramoElegido || lineaElegida)
        ? 'Mostrando las clases del ramo y la línea elegidos.'
        : ayudaValor;
    return (
      <SearchableSelect
        label={camposFiltroInforme(moduloCatalogo(modulo)).find((campo) => campo.id === filtro.fieldId)?.label}
        size="small"
        allowEmpty
        emptyOptionLabel="Todos"
        loading={filtro.fieldId === 'ramo' ? cargandoRamos : filtro.fieldId === 'linea' ? cargandoLineas : cargandoClases}
        value={filtro.value.trim() || null}
        onChange={(value) => onChange({ operator: 'equals', value: value == null ? '' : String(value) })}
        options={nombres.map((nombre) => ({ value: nombre, label: nombre }))}
        helperText={ayuda}
        sx={filtroFieldSx}
      />
    );
  }

  if (filtro.fieldId === 'cantidad' || filtro.fieldId === 'total' || campoLegacy?.type === 'number' || campoLegacy?.type === 'date') {
    const tipo = filtro.fieldId === 'cantidad' || filtro.fieldId === 'total' || campoLegacy?.type === 'number' ? 'number' : 'date';
    return (
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
        <TextField
          fullWidth
          size="small"
          label={filtro.operator === 'between' ? 'Desde' : 'Valor'}
          type={tipo}
          value={filtro.value}
          error={errorValor}
          helperText={ayudaValor}
          InputLabelProps={tipo === 'date' ? { shrink: true } : undefined}
          inputProps={tipo === 'number' ? { step: 'any' } : undefined}
          onChange={(event) => onChange({ value: event.target.value })}
          sx={filtroFieldSx}
        />
        {filtro.operator === 'between' && (
          <TextField
            fullWidth
            size="small"
            label="Hasta"
            type={tipo}
            value={filtro.valorHasta ?? ''}
            error={Boolean(errorDe(`filtros[${index}].valorHasta`))}
            helperText={errorDe(`filtros[${index}].valorHasta`)}
            InputLabelProps={tipo === 'date' ? { shrink: true } : undefined}
            inputProps={tipo === 'number' ? { step: 'any' } : undefined}
            onChange={(event) => onChange({ valorHasta: event.target.value })}
            sx={filtroFieldSx}
          />
        )}
      </Stack>
    );
  }

  return (
    <TextField
      fullWidth
      size="small"
      label="Valor"
      value={filtro.value}
      error={errorValor}
      helperText={ayudaValor}
      onChange={(event) => onChange({ value: event.target.value })}
      sx={filtroFieldSx}
    />
  );
};

interface ArticuloFiltroFieldProps {
  valor: string;
  onElegir: (codigo: string, descripcion: string) => void;
  onAbrirModal: () => void;
}

const ArticuloFiltroField: React.FC<ArticuloFiltroFieldProps> = ({ valor, onElegir, onAbrirModal }) => {
  const articulo = leerArticulo(valor);
  const etiqueta = etiquetaArticulo(articulo);
  const seleccionado = useMemo<Producto | null>(() => (
    articulo.codigo
      ? { codigoArticulo: articulo.codigo, descripcion: articulo.descripcion }
      : null
  ), [articulo.codigo, articulo.descripcion]);
  const [entrada, setEntrada] = useState(etiqueta);
  const [opciones, setOpciones] = useState<Producto[]>([]);
  const [cargando, setCargando] = useState(false);
  const [enfocado, setEnfocado] = useState(false);

  useEffect(() => {
    if (!enfocado) setEntrada(etiqueta);
  }, [enfocado, etiqueta]);

  useEffect(() => {
    const termino = entrada.trim();
    if (!enfocado || termino.length === 0 || termino === etiqueta) {
      setOpciones(seleccionado ? [seleccionado] : []);
      setCargando(false);
      return undefined;
    }
    let cancelado = false;
    const timer = setTimeout(() => {
      setCargando(true);
      productoService.getProductos({
        searchTerm: termino,
        pageNumber: 1,
        pageSize: 20,
        ...PARAMS_PRODUCTO_OPERATIVO,
      }).then((resultado) => {
        if (!cancelado) setOpciones(resultado.items ?? []);
      }).catch(() => {
        if (!cancelado) setOpciones([]);
      }).finally(() => {
        if (!cancelado) setCargando(false);
      });
    }, 350);
    return () => {
      cancelado = true;
      clearTimeout(timer);
    };
  }, [enfocado, entrada, etiqueta, seleccionado]);

  const opcionesVisibles = useMemo(() => {
    if (!seleccionado) return opciones;
    if (opciones.some((opcion) => opcion.codigoArticulo === seleccionado.codigoArticulo)) return opciones;
    return [seleccionado, ...opciones];
  }, [opciones, seleccionado]);

  return (
    <Autocomplete
      fullWidth
      size="small"
      options={opcionesVisibles}
      loading={cargando}
      value={seleccionado}
      inputValue={entrada}
      onFocus={() => setEnfocado(true)}
      onBlur={() => setEnfocado(false)}
      onInputChange={(_, value, reason) => {
        if (reason === 'reset') return;
        setEntrada(value);
      }}
      onChange={(_, option) => {
        if (!option) {
          onElegir('', '');
          setEntrada('');
          return;
        }
        onElegir(option.codigoArticulo, option.descripcion ?? '');
        setEntrada(etiquetaArticulo({ codigo: option.codigoArticulo, descripcion: option.descripcion ?? '' }));
      }}
      getOptionLabel={(option) => etiquetaArticulo({
        codigo: option.codigoArticulo,
        descripcion: option.descripcion ?? '',
      })}
      isOptionEqualToValue={(option, current) => option.codigoArticulo === current.codigoArticulo}
      filterOptions={(items) => items}
      noOptionsText={entrada.trim() ? 'Sin artículos' : 'Escribí código o descripción'}
      renderInput={(params) => (
        <TextField
          {...params}
          label="Artículo"
          placeholder="Código o descripción"
          sx={filtroFieldSx}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {cargando ? <CircularProgress color="inherit" size={16} /> : null}
                {params.InputProps.endAdornment}
                <InputAdornment position="end">
                  <IconButton
                    edge="end"
                    size="small"
                    color="primary"
                    title="Buscar artículo"
                    onMouseDown={(event) => event.preventDefault()}
                    onClick={onAbrirModal}
                  >
                    <ArrowDropDownIcon />
                  </IconButton>
                </InputAdornment>
              </>
            ),
          }}
        />
      )}
    />
  );
};

export default FiltrosAdicionalesVentas;
